"use client";

import "@/shared/styles/carevinco-wireframe.scss";
import { lazy, Suspense, useCallback, useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import type { AuthDialogMode, RegistrationRole } from "@/modules/auth/components/AuthDialog";
import { InquiryModal } from "@/modules/landing/components/InquiryModal";
import { LandingHeader } from "@/modules/landing/components/LandingHeader";
import CorrectedLandingContent from "@/modules/landing/components/CorrectedLandingContent";
import { CorrectedMarketingFooter } from "@/modules/landing/components/CorrectedMarketingFooter";
import { WireframeAbout, WireframeBenefits, WireframeFooter as WireframeFooterContent } from "@/modules/landing/components/WireframeStaticSections";
import { carevincoSupportContent } from "@/modules/landing/content/carevincoInformation";
import AppStartupScreen from "@/shared/components/AppStartupScreen";
import { coreServices } from "@/shared/config/coreServices";
import { isElderlyUserRole } from "@/shared/lib/roles";
import { PortalLanguageProvider } from "@/shared/providers/PortalLanguageProvider";
import { useAuthStore, type AppRole } from "@/shared/store/authStore";

const PublicAuthDialog = lazy(() => import("@/modules/landing/components/PublicAuthDialog"));
const ProviderWireframeFooter = lazy(() => import("@/modules/landing/components/ProviderWireframeFooter"));
const AdminDashboard = dynamic(() => import("@/modules/admin/pages/AdminDashboard").then((module) => module.AdminDashboard), { ssr: false });
const ProviderDashboard = dynamic(() => import("@/modules/provider/pages/ProviderDashboard").then((module) => module.ProviderDashboard), { ssr: false });
const ElderlyPortalLayout = dynamic(() => import("@/modules/shell/layouts/ElderlyPortalLayout"), { ssr: false });
const SuperAdminDashboard = dynamic(() => import("@/modules/super-admin/pages/SuperAdminDashboard").then((module) => module.SuperAdminDashboard), { ssr: false });
const UserDashboard = dynamic(() => import("@/modules/user/pages/UserDashboard").then((module) => module.UserDashboard), { ssr: false });
const AccountDashboardStyles = dynamic(() => import("@/shared/providers/AccountDashboardStyles"), { ssr: false });
const UserDashboardStyles = dynamic(() => import("@/shared/providers/UserDashboardStyles"), { ssr: false });

const GOOGLE_PLAY_URL = process.env.NEXT_PUBLIC_GOOGLE_PLAY_URL || "https://play.google.com/store/search?q=Carevinco&c=apps";
const APP_STORE_URL = process.env.NEXT_PUBLIC_APP_STORE_URL || "https://apps.apple.com/us/iphone/search?term=Carevinco";

function preferredScrollBehavior(): ScrollBehavior {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
}

function useAppShellReady() {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let isCancelled = false;
    let fallbackTimer: number | undefined;

    async function prepareShell() {
      try {
        const criticalAssets = [
          "/assets/brand/logo-mark-7cc959d9-5283-46c3-92ec-5f8be3d1ffea.svg",
          "/assets/brand/logo-type-493879ef-c62f-4114-b3fe-6882487aadbc.svg",
          "/assets/landingHeader/portal-icon-e066ad3f-9b90-4373-be93-cecb5fa71eec.svg",
        ];
        const assetReadiness = Promise.all(
          criticalAssets.map(
            (source) =>
              new Promise<void>((resolve) => {
                const image = new window.Image();
                image.onload = () => resolve();
                image.onerror = () => resolve();
                image.src = source;
                if (image.complete) resolve();
              }),
          ),
        );
        const fontReadiness = "fonts" in document ? document.fonts.ready : Promise.resolve();

        await Promise.race([
          Promise.all([fontReadiness, assetReadiness]),
          new Promise<void>((resolve) => {
            fallbackTimer = window.setTimeout(resolve, 1_500);
          }),
        ]);
      } catch {
        // The startup screen remains the safe visual fallback if readiness checks are unavailable.
      }

      await new Promise<void>((resolve) => {
        window.requestAnimationFrame(() => window.requestAnimationFrame(() => resolve()));
      });

      if (!isCancelled) {
        setIsReady(true);
      }
    }

    void prepareShell();
    return () => {
      isCancelled = true;
      if (fallbackTimer != null) window.clearTimeout(fallbackTimer);
    };
  }, []);

  return isReady;
}

function useLandingAuthDialog() {
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [authDialogMode, setAuthDialogMode] = useState<AuthDialogMode>("login");
  const [authDialogRole, setAuthDialogRole] = useState<RegistrationRole>("elderly");

  const openAuthDialog = useCallback((mode: AuthDialogMode, role: RegistrationRole = "elderly") => {
    setAuthDialogMode(mode);
    setAuthDialogRole(role);
    setAuthDialogOpen(true);
  }, []);

  const closeAuthDialog = useCallback(() => {
    setAuthDialogOpen(false);
  }, []);

  return {
    authDialogMode,
    authDialogOpen,
    authDialogRole,
    closeAuthDialog,
    openAuthDialog,
  };
}

function LandingAuthQuerySync({
  onAuthRequest,
}: {
  onAuthRequest: (mode: AuthDialogMode, role?: RegistrationRole) => void;
}) {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const searchParamsValue = searchParams.toString();

  useEffect(() => {
    const currentParams = new URLSearchParams(searchParamsValue);
    const authMode = currentParams.get("auth") as AuthDialogMode | null;
    const role = currentParams.get("role");

    if (authMode !== "login" && authMode !== "register" && authMode !== "forgot") {
      return;
    }

    onAuthRequest(authMode, role === "provider" ? "provider" : "elderly");

    currentParams.delete("auth");
    currentParams.delete("role");
    const nextQuery = currentParams.toString();
    router.replace(
      `${pathname}${nextQuery ? `?${nextQuery}` : ""}${window.location.hash}`,
      { scroll: false },
    );
  }, [onAuthRequest, pathname, router, searchParamsValue]);

  return null;
}

function RoleDashboardBody({ role }: { role?: AppRole | null }) {
  if (isElderlyUserRole(role)) {
    return <UserDashboard />;
  }

  if (role === "provider") {
    return <ProviderDashboard />;
  }

  if (role === "admin") {
    return <AdminDashboard />;
  }

  if (role === "super_admin") {
    return <SuperAdminDashboard />;
  }

  return <UserDashboard />;
}

function scrollMarketingHashIntoView(hash: string) {
  const targetId = hash.replace("#", "");
  const target = document.getElementById(targetId);

  if (!target) {
    return false;
  }

  const header = document.querySelector(".cv-wireframe .site-header, .cv-wireframe [data-marketing-header]") as HTMLElement | null;
  const headerOffset = header?.getBoundingClientRect().height ?? 0;
  const targetTop = window.scrollY + target.getBoundingClientRect().top - headerOffset - 10;

  window.scrollTo({ top: Math.max(targetTop, 0), behavior: preferredScrollBehavior() });
  return true;
}

function useWireframeInteractions(rootRef: RefObject<HTMLElement | null>, openInquiry: () => void) {
  useEffect(() => {
    const root = rootRef.current;

    if (!root) {
      return undefined;
    }

    function handleClick(event: MouseEvent) {
      const target = event.target as HTMLElement | null;

      if (!target) {
        return;
      }

      const inquiryTrigger = target.closest("[data-open-inquiry]");

      if (inquiryTrigger) {
        event.preventDefault();
        openInquiry();
        return;
      }

      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      const href = anchor?.getAttribute("href") ?? "";
      const shouldSmoothScrollLocalHash = href.startsWith("#") || (href.startsWith("/#") && window.location.pathname === "/");

      if (anchor && shouldSmoothScrollLocalHash) {
        const hash = href.startsWith("/#") ? href.slice(1) : href;

        if (scrollMarketingHashIntoView(hash)) {
          event.preventDefault();
          window.history.pushState(null, "", href.startsWith("/#") ? href : `${window.location.pathname}${hash}`);
          return;
        }
      }

      const faqButton = target.closest("[data-accordion] .faq-item button");

      if (faqButton) {
        const item = faqButton.closest(".faq-item");
        const accordion = faqButton.closest("[data-accordion]");

        if (item && accordion) {
          accordion.querySelectorAll(".faq-item").forEach((node) => {
            if (node !== item) {
              node.classList.remove("is-open");
            }
          });
          item.classList.toggle("is-open");
        }
      }
    }

    root.addEventListener("click", handleClick);

    return () => root.removeEventListener("click", handleClick);
  }, [openInquiry, rootRef]);
}

function BackToTopButton() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    function updateBackToTop() {
      const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollProgress = scrollableHeight > 0 ? window.scrollY / scrollableHeight : 0;
      setVisible(scrollProgress >= 0.3);
    }

    updateBackToTop();
    window.addEventListener("scroll", updateBackToTop, { passive: true });
    window.addEventListener("resize", updateBackToTop);

    return () => {
      window.removeEventListener("scroll", updateBackToTop);
      window.removeEventListener("resize", updateBackToTop);
    };
  }, []);

  return (
    <button
      className={`back-to-top${visible ? " is-visible" : ""}`}
      type="button"
      aria-label="Back to Top"
      onClick={() => window.scrollTo({ top: 0, behavior: preferredScrollBehavior() })}
    >
      <span className="back-to-top__icon" aria-hidden="true">
        ↑
      </span>
      <span className="back-to-top__label">Back to Top</span>
    </button>
  );
}

function WireframeFooter({ role, showInquiry }: { role?: AppRole | null; showInquiry?: boolean } = {}) {
  if (role === "provider") {
    return (
      <Suspense fallback={null}>
        <ProviderWireframeFooter appStoreUrl={APP_STORE_URL} googlePlayUrl={GOOGLE_PLAY_URL} showInquiry={showInquiry} />
      </Suspense>
    );
  }

  return <WireframeFooterContent appStoreUrl={APP_STORE_URL} googlePlayUrl={GOOGLE_PLAY_URL} role={role} showInquiry={showInquiry} />;
}

type MarketingVariant = "legacy" | "corrected";

function WireframeInteractiveShell({
  children,
  includeBackToTop = true,
  variant = "legacy",
}: {
  children: ReactNode;
  includeBackToTop?: boolean;
  variant?: MarketingVariant;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [inquiryOpen, setInquiryOpen] = useState(false);

  useWireframeInteractions(rootRef, () => setInquiryOpen(true));

  return (
    <div className={`cv-wireframe${variant === "corrected" ? " corrected-public-pages" : ""}`} ref={rootRef}>
      {children}
      <InquiryModal open={inquiryOpen} onClose={() => setInquiryOpen(false)} />
      {includeBackToTop ? <BackToTopButton /> : null}
    </div>
  );
}

function WireframePageChrome({ children, variant = "legacy" }: { children: ReactNode; variant?: MarketingVariant }) {
  const appShellReady = useAppShellReady();
  const hasHydrated = useAuthStore((state) => state.hasHydrated);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isAuthRouting = useAuthStore((state) => state.isAuthRouting);
  const isLogoutPending = useAuthStore((state) => state.isLogoutPending);
  const role = useAuthStore((state) => state.role);
  const router = useRouter();
  const { authDialogMode, authDialogOpen, authDialogRole, closeAuthDialog, openAuthDialog } = useLandingAuthDialog();

  useEffect(() => {
    if (!hasHydrated || !isLogoutPending || isAuthenticated) {
      return;
    }

    router.replace("/#home");
  }, [hasHydrated, isAuthenticated, isLogoutPending, router]);

  if (hasHydrated && isAuthenticated && !appShellReady) {
    return <AppStartupScreen message="Preparing your Carevinco experience..." title="Loading Carevinco" />;
  }

  if (hasHydrated && isAuthRouting) {
    return <AppStartupScreen message="Preparing your secure workspace..." title="Loading Carevinco" />;
  }

  if (hasHydrated && isLogoutPending) {
    return <AppStartupScreen message="Taking you to the Carevinco home page..." title="Signing you out" />;
  }

  return (
    <WireframeInteractiveShell variant={variant}>
      <Suspense fallback={null}>
        <LandingAuthQuerySync onAuthRequest={openAuthDialog} />
      </Suspense>
      <div className={`page-shell page-shell--with-header${variant === "corrected" ? " corrected-public-shell" : ""}`}>
        <LandingHeader authDialogOpen={authDialogOpen} onAuthRequest={openAuthDialog} />
        {children}
        {variant === "corrected" && !isAuthenticated ? (
          <CorrectedMarketingFooter appStoreUrl={APP_STORE_URL} googlePlayUrl={GOOGLE_PLAY_URL} />
        ) : (
          <WireframeFooter role={isAuthenticated ? role : undefined} showInquiry={!isAuthenticated} />
        )}
      </div>
      {authDialogOpen ? (
        <Suspense fallback={null}>
          <PublicAuthDialog initialMode={authDialogMode} initialRole={authDialogRole} onClose={closeAuthDialog} />
        </Suspense>
      ) : null}
    </WireframeInteractiveShell>
  );
}

const serviceCardMeta: Record<string, { benefit: string; icon: "medical" | "coordination" | "telehealth" | "companion" }> = {
  "nurse-services": {
    benefit: "Personalized medical attention at home",
    icon: "medical",
  },
  "care-coordinator-services": {
    benefit: "Hassle-free healthcare coordination",
    icon: "coordination",
  },
  "tele-health-services": {
    benefit: "Accessible healthcare support from home",
    icon: "telehealth",
  },
  "companion-ca-services": {
    benefit: "Reduces loneliness and isolation",
    icon: "companion",
  },
};

function ServiceCardIcon({ icon }: { icon: "medical" | "coordination" | "telehealth" | "companion" }) {
  if (icon === "coordination") {
    return (
      <svg aria-hidden="true" viewBox="0 0 24 24">
        <path d="M8 7.5a3 3 0 1 0-3 3 3 3 0 0 0 3-3Z" />
        <path d="M19 7.5a3 3 0 1 0-3 3 3 3 0 0 0 3-3Z" />
        <path d="M7.5 17.5a4.5 4.5 0 0 1 9 0" />
        <path d="M9 8h4" />
        <path d="M12 12v2" />
      </svg>
    );
  }

  if (icon === "telehealth") {
    return (
      <svg aria-hidden="true" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="8" />
        <path d="M4 12h16" />
        <path d="M12 4a12 12 0 0 1 0 16" />
        <path d="M12 4a12 12 0 0 0 0 16" />
      </svg>
    );
  }

  if (icon === "companion") {
    return (
      <svg aria-hidden="true" viewBox="0 0 24 24">
        <path d="M7 12.5V6.75a2.25 2.25 0 0 1 4.5 0V11" />
        <path d="M11.5 10.5V5.75a2.25 2.25 0 0 1 4.5 0v6" />
        <path d="M16 10.5V8.75a2 2 0 0 1 4 0v4.5A7.75 7.75 0 0 1 12.25 21H12a7 7 0 0 1-5.4-2.55L3.9 15.2a2 2 0 0 1 2.9-2.75L9 14.5" />
        <path d="M17.5 4.5c1.9-2.4 5 1 2.2 3.3" />
      </svg>
    );
  }

  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <rect x="5" y="6" width="14" height="13" rx="2" />
      <path d="M9 6V4h6v2" />
      <path d="M12 10v5" />
      <path d="M9.5 12.5h5" />
    </svg>
  );
}

export function ServicesPageContent() {
  return (
    <main id="services">
      <section className="care-services-section">
        <div className="container">
          <div className="section-title section-title--center care-services-title">
            <p>Our Caring Services</p>
            <h1>
              Flexible Home Care Services for <span>Every Family Need</span>
            </h1>
            <small>These service categories already map to the existing Carevinco booking and provider flow, so the new design stays aligned with the real application.</small>
          </div>

          <div className="care-services-grid">
            {coreServices.map((service) => {
              const meta = serviceCardMeta[service.slug] ?? { benefit: service.benefits[0] ?? "Carevinco service support", icon: "medical" as const };

              return (
                <article className="care-service-card" key={service.slug}>
                  <div className="care-service-kicker">
                    <span className="care-service-icon">
                      <ServiceCardIcon icon={meta.icon} />
                    </span>
                    <span>Carevinco Service</span>
                  </div>

                  <h2>{service.title}</h2>
                  {/* <p>{service.summary}</p> */}

                  <ul>
                    {service.offerings.slice(0, 4).map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>

                  <div className="care-service-detail-row">
                    <span>{meta.benefit}</span>
                    <Link href={`/services/${service.slug}`}>View service details</Link>
                  </div>

                  <div className="care-service-actions">
                    <Link className="btn btn--outline" href={`/services/${service.slug}`}>
                      Learn More
                    </Link>
                    <button className="btn btn--primary" type="button" data-open-inquiry>
                      <span aria-hidden="true" className="care-service-calendar-icon">▣</span>
                      Book This Service
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>
    </main>
  );
}

export function BenefitsPageContent({ asSection = false }: { asSection?: boolean; showPromise?: boolean } = {}) {
  if (asSection) {
    return <WireframeBenefits googlePlayUrl={GOOGLE_PLAY_URL} appStoreUrl={APP_STORE_URL} />;
  }

  return <WireframeBenefits googlePlayUrl={GOOGLE_PLAY_URL} appStoreUrl={APP_STORE_URL} />;
}

export function AboutPageContent() {
  return <WireframeAbout />;
}

export function ContactPageContent() {
  const { channels, description, eyebrow, titleAccent, titleLead } = carevincoSupportContent;

  return (
    <main id="contact-page">
      <section className="wireframe-service-hero">
        <div className="container">
          <div className="section-title section-title--center">
            <p>{eyebrow}</p>
            <h1>{titleLead}<span>{titleAccent}</span></h1>
            <small>{description}</small>
          </div>
          <div className="wireframe-contact-grid">
            {channels.map((channel) => (
              <article className="wireframe-contact-panel" key={channel.id}>
                <h3>{channel.title}</h3>
                <p>{channel.description}</p>
                <a
                  className="text-link"
                  href={channel.href}
                  {...(channel.external ? { rel: "noopener noreferrer", target: "_blank" } : {})}
                >
                  {channel.value}
                </a>
              </article>
            ))}
          </div>
          <div style={{ marginTop: 42, textAlign: "center" }}>
            <button className="btn btn--primary" type="button" data-open-inquiry>
              Book a Care Visit
            </button>
          </div>
        </div>
      </section>
      <WireframeBenefits googlePlayUrl={GOOGLE_PLAY_URL} appStoreUrl={APP_STORE_URL} />
    </main>
  );
}

export function LandingFooter({ embedded = false, role, showInquiry }: { embedded?: boolean; role?: AppRole | null; showInquiry?: boolean } = {}) {
  const footer = <WireframeFooter role={role} showInquiry={showInquiry} />;

  if (embedded) {
    return footer;
  }

  return (
    <WireframeInteractiveShell includeBackToTop={false}>
      <div className="wireframe-footer-shell">{footer}</div>
    </WireframeInteractiveShell>
  );
}

export function MarketingPageShell({ children, variant = "legacy" }: { children: ReactNode; variant?: MarketingVariant }) {
  return <WireframePageChrome variant={variant}>{children}</WireframePageChrome>;
}

export default function LandingPage() {
  const appShellReady = useAppShellReady();
  const hasHydrated = useAuthStore((state) => state.hasHydrated);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isAuthRouting = useAuthStore((state) => state.isAuthRouting);
  const isLogoutPending = useAuthStore((state) => state.isLogoutPending);
  const role = useAuthStore((state) => state.role);
  const setLogoutPending = useAuthStore((state) => state.setLogoutPending);
  const router = useRouter();
  const { authDialogMode, authDialogOpen, authDialogRole, closeAuthDialog, openAuthDialog } = useLandingAuthDialog();

  useEffect(() => {
    if (!hasHydrated || !isLogoutPending || isAuthenticated) {
      return undefined;
    }

    router.replace("/#home");
    const resetTimer = window.setTimeout(() => setLogoutPending(false), 180);

    return () => window.clearTimeout(resetTimer);
  }, [hasHydrated, isAuthenticated, isLogoutPending, router, setLogoutPending]);

  if (hasHydrated && isAuthenticated && !appShellReady) {
    return <AppStartupScreen message="Preparing your Carevinco experience..." title="Loading Carevinco" />;
  }

  if (hasHydrated && isAuthRouting) {
    return <AppStartupScreen message="Preparing your secure workspace..." title="Loading Carevinco" />;
  }

  if (hasHydrated && isLogoutPending) {
    return <AppStartupScreen message="Taking you to the Carevinco home page..." title="Signing you out" />;
  }

  if (hasHydrated && isAuthenticated && isElderlyUserRole(role)) {
    return (
      <>
        <UserDashboardStyles />
        <Suspense fallback={null}>
          <LandingAuthQuerySync onAuthRequest={openAuthDialog} />
        </Suspense>
        <PortalLanguageProvider>
          <ElderlyPortalLayout>
            <UserDashboard />
          </ElderlyPortalLayout>
        </PortalLanguageProvider>
      </>
    );
  }

  if (hasHydrated && isAuthenticated) {
    return (
      <div className="cv-wireframe dashboard-wireframe home-shell dashboard-home-shell">
        <AccountDashboardStyles />
        <Suspense fallback={null}>
          <LandingAuthQuerySync onAuthRequest={openAuthDialog} />
        </Suspense>
        <div className="page-shell page-shell--with-header dashboard-page-shell">
          <LandingHeader authDialogOpen={authDialogOpen} onAuthRequest={openAuthDialog} />
          <main className="dashboard-home-main dashboard-wireframe-main">
            <div className="dashboard-wireframe-main__content">
              <RoleDashboardBody role={role} />
            </div>
          </main>
          <LandingFooter embedded role={role} showInquiry={false} />
        </div>
        {authDialogOpen ? (
          <Suspense fallback={null}>
            <PublicAuthDialog initialMode={authDialogMode} initialRole={authDialogRole} onClose={closeAuthDialog} />
          </Suspense>
        ) : null}
      </div>
    );
  }

  return (
    <WireframePageChrome variant="corrected">
      <CorrectedLandingContent />
    </WireframePageChrome>
  );
}
